import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Icon } from '~/components/ui/Icon';
import { useWorkspaceLocale } from '~/stores/workspace';

type DocumentationLocale = 'en' | 'ru' | 'zh-Hans';
type EndpointId = 'images' | 'videos' | 'tasks';
type CopiedCode = `${EndpointId}-request` | `${EndpointId}-response`;

type DocumentationCopy = {
  eyebrow: string;
  title: string;
  description: string;
  back: string;
  skipToReference: string;
  downloadOpenApi: string;
  endpoints: string;
  authentication: string;
  authDescription: string;
  authenticationCode: string;
  request: string;
  response: string;
  notes: string;
  copy: string;
  copied: string;
  imageNavigationTitle: string;
  videoNavigationTitle: string;
  taskNavigationTitle: string;
  imageTitle: string;
  imageDescription: string;
  videoTitle: string;
  videoDescription: string;
  taskTitle: string;
  taskDescription: string;
  imageNotes: string[];
  videoNotes: string[];
  taskNotes: string[];
};

const copy: Record<DocumentationLocale, DocumentationCopy> = {
  'zh-Hans': {
    eyebrow: '开发者文档',
    title: 'MyloStudio API 参考',
    description: '使用工作空间积分，从你的服务端生成图片和视频。',
    back: '返回 API 密钥',
    skipToReference: '跳转到 API 参考',
    downloadOpenApi: '下载 OpenAPI 文件',
    endpoints: '端点',
    authentication: '鉴权与幂等',
    authDescription:
      '所有请求使用 API 密钥作为 Bearer Token；每一次创建任务都必须提供唯一的 Idempotency-Key。密钥只能保存在服务端。',
    authenticationCode:
      'Authorization: Bearer $MYLO_API_KEY\nIdempotency-Key: request-001',
    request: '请求示例',
    response: '响应示例',
    notes: '使用说明',
    copy: '复制',
    copied: '已复制',
    imageNavigationTitle: '图片生成',
    videoNavigationTitle: '视频生成',
    taskNavigationTitle: '查询任务',
    imageTitle: '生成图片',
    imageDescription: '异步创建一张图片生成任务。',
    videoTitle: '生成视频',
    videoDescription: '异步创建一个 Мыло 视频生成任务。',
    taskTitle: '查询任务',
    taskDescription: '轮询任务状态并获取短时有效的结果链接。',
    imageNotes: [
      '需要 image:generate 权限。',
      '支持 prompt 和可选的 aspectRatio。',
    ],
    videoNotes: [
      '需要 video:generate 和 task:read 权限。',
      '需要有效的付费套餐；duration 范围为 4–30 秒。',
      'audioAssetId 必须是当前工作空间中已就绪的音频资产；它仅作为 Мыло 视频参考，不保证保留原音轨。',
      '结果 type 始终为 video，不会降级为图片。',
    ],
    taskNotes: [
      '使用创建任务的同一 API 密钥查询。',
      '状态为 succeeded 后，outputUrl 是短时有效的签名链接。',
    ],
  },
  en: {
    eyebrow: 'Developer documentation',
    title: 'MyloStudio API reference',
    description:
      'Generate images and videos from your server with workspace credits.',
    back: 'Back to API keys',
    skipToReference: 'Skip to API reference',
    downloadOpenApi: 'Download OpenAPI file',
    endpoints: 'Endpoints',
    authentication: 'Authentication & idempotency',
    authDescription:
      'Authenticate every request with your API key as a Bearer token. Each task creation also needs a unique Idempotency-Key. Keep keys on your server only.',
    authenticationCode:
      'Authorization: Bearer $MYLO_API_KEY\nIdempotency-Key: request-001',
    request: 'Request example',
    response: 'Response example',
    notes: 'Notes',
    copy: 'Copy',
    copied: 'Copied',
    imageNavigationTitle: 'Image generation',
    videoNavigationTitle: 'Video generation',
    taskNavigationTitle: 'Get a task',
    imageTitle: 'Generate an image',
    imageDescription: 'Create an asynchronous image-generation task.',
    videoTitle: 'Generate a video',
    videoDescription: 'Create an asynchronous Мыло Video generation task.',
    taskTitle: 'Get a task',
    taskDescription: 'Poll a task and retrieve its short-lived result URL.',
    imageNotes: [
      'Requires the image:generate scope.',
      'Accepts prompt and an optional aspectRatio.',
    ],
    videoNotes: [
      'Requires video:generate and task:read scopes.',
      'Requires an active paid plan; duration ranges from 4 to 30 seconds.',
      'audioAssetId must be a ready audio asset in this Workspace. Мыло Video uses it as a reference and does not guarantee the original track in the result.',
      'The resulting task type is always video and never falls back to image.',
    ],
    taskNotes: [
      'Use the same API key that created the task.',
      'After succeeded, outputUrl is a short-lived signed URL.',
    ],
  },
  ru: {
    eyebrow: 'Документация для разработчиков',
    title: 'Справочник MyloStudio API',
    description:
      'Создавайте изображения и видео с сервера, используя кредиты рабочего пространства.',
    back: 'К API-ключам',
    skipToReference: 'Перейти к справочнику API',
    downloadOpenApi: 'Скачать OpenAPI-файл',
    endpoints: 'Методы',
    authentication: 'Авторизация и идемпотентность',
    authDescription:
      'Передавайте API-ключ в каждом запросе как Bearer Token. Для создания каждой задачи также нужен уникальный Idempotency-Key. Храните ключи только на сервере.',
    authenticationCode:
      'Authorization: Bearer $MYLO_API_KEY\nIdempotency-Key: request-001',
    request: 'Пример запроса',
    response: 'Пример ответа',
    notes: 'Примечания',
    copy: 'Копировать',
    copied: 'Скопировано',
    imageNavigationTitle: 'Генерация изображений',
    videoNavigationTitle: 'Генерация видео',
    taskNavigationTitle: 'Получить задачу',
    imageTitle: 'Создать изображение',
    imageDescription: 'Создаёт асинхронную задачу генерации изображения.',
    videoTitle: 'Создать видео',
    videoDescription: 'Создаёт асинхронную задачу генерации Мыло Видео.',
    taskTitle: 'Получить задачу',
    taskDescription:
      'Проверяйте статус задачи и получайте временную ссылку на результат.',
    imageNotes: [
      'Требуется scope image:generate.',
      'Поддерживаются prompt и необязательный aspectRatio.',
    ],
    videoNotes: [
      'Требуются scopes video:generate и task:read.',
      'Нужен активный платный тариф; duration — от 4 до 30 секунд.',
      'audioAssetId должен ссылаться на готовый аудио-ресурс в этом рабочем пространстве. Мыло Видео использует его как референс и не гарантирует сохранение исходной дорожки.',
      'У результата всегда type: video — он не заменяется изображением.',
    ],
    taskNotes: [
      'Используйте тот же API-ключ, которым была создана задача.',
      'После succeeded outputUrl — временная подписанная ссылка.',
    ],
  },
};

const endpoints: Record<
  EndpointId,
  { method: 'POST' | 'GET'; path: string; accent: string }
> = {
  images: {
    method: 'POST',
    path: '/v1/images/generations',
    accent: 'bg-sky-500/10 text-sky-700 dark:text-sky-300',
  },
  videos: {
    method: 'POST',
    path: '/v1/videos/generations',
    accent: 'bg-primary/10 text-primary',
  },
  tasks: {
    method: 'GET',
    path: '/v1/tasks/:id',
    accent: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  },
};

const examples: Record<EndpointId, { request: string; response: string }> = {
  images: {
    request: `curl -X POST https://<region-api-host>/v1/images/generations \\
  -H "Authorization: Bearer $MYLO_API_KEY" \\
  -H "Idempotency-Key: image-001" \\
  -H "Content-Type: application/json" \\
  -d '{"prompt":"A cinematic product photograph","aspectRatio":"1:1"}'`,
    response: `{"id":"task_uuid","type":"image","status":"queued","reservedCredits":"12"}`,
  },
  videos: {
    request: `curl -X POST https://<region-api-host>/v1/videos/generations \\
  -H "Authorization: Bearer $MYLO_API_KEY" \\
  -H "Idempotency-Key: video-001" \\
  -H "Content-Type: application/json" \\
  -d '{"prompt":"A playful four-second short film guided by the reference audio","audioAssetId":"<ready-audio-asset-uuid>","duration":4,"ratio":"16:9"}'`,
    response: `{"id":"task_uuid","type":"video","status":"queued","reservedCredits":"48"}`,
  },
  tasks: {
    request: `curl https://<region-api-host>/v1/tasks/task_uuid \\
  -H "Authorization: Bearer $MYLO_API_KEY"`,
    response: `{"id":"task_uuid","type":"video","status":"succeeded","outputUrl":"https://..."}`,
  },
};

const useDocumentationCopy = (): DocumentationCopy => {
  const locale = useWorkspaceLocale();
  return copy[locale as DocumentationLocale] ?? copy.en;
};

const endpointOrder: EndpointId[] = ['images', 'videos', 'tasks'];

export const ApiDocumentationPage = (): React.ReactElement => {
  const text = useDocumentationCopy();
  const [searchParams] = useSearchParams();
  const requestedEndpoint = searchParams.get('endpoint');
  const initialEndpoint: EndpointId =
    requestedEndpoint === 'images' ||
    requestedEndpoint === 'videos' ||
    requestedEndpoint === 'tasks'
      ? requestedEndpoint
      : 'images';
  const [copied, setCopied] = useState<CopiedCode | null>(null);
  const [activeEndpoint, setActiveEndpoint] =
    useState<EndpointId>(initialEndpoint);
  const metadata: Record<
    EndpointId,
    {
      navigationTitle: string;
      title: string;
      description: string;
      notes: string[];
    }
  > = {
    images: {
      navigationTitle: text.imageNavigationTitle,
      title: text.imageTitle,
      description: text.imageDescription,
      notes: text.imageNotes,
    },
    videos: {
      navigationTitle: text.videoNavigationTitle,
      title: text.videoTitle,
      description: text.videoDescription,
      notes: text.videoNotes,
    },
    tasks: {
      navigationTitle: text.taskNavigationTitle,
      title: text.taskTitle,
      description: text.taskDescription,
      notes: text.taskNotes,
    },
  };

  useEffect(() => {
    if (!requestedEndpoint || !endpointOrder.includes(initialEndpoint)) return;
    window.requestAnimationFrame(() => {
      document.getElementById(initialEndpoint)?.scrollIntoView({
        block: 'start',
      });
    });
  }, [initialEndpoint, requestedEndpoint]);

  useEffect(() => {
    const updateActiveEndpoint = (): void => {
      const next = endpointOrder.reduce<EndpointId>((current, id) => {
        const top = document.getElementById(id)?.getBoundingClientRect().top;
        return top !== undefined && top <= 180 ? id : current;
      }, endpointOrder[0] ?? 'images');
      setActiveEndpoint(next);
    };
    updateActiveEndpoint();
    window.addEventListener('scroll', updateActiveEndpoint, { passive: true });
    return () => window.removeEventListener('scroll', updateActiveEndpoint);
  }, []);

  const copyCode = async (
    endpointId: EndpointId,
    kind: 'request' | 'response'
  ): Promise<void> => {
    try {
      await navigator.clipboard.writeText(examples[endpointId][kind]);
      setCopied(`${endpointId}-${kind}`);
      window.setTimeout(() => setCopied(null), 1800);
    } catch {
      setCopied(null);
    }
  };

  return (
    <div className="min-h-screen bg-surface text-foreground">
      <a
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-20 focus:rounded-lg focus:bg-surface focus:px-4 focus:py-2 focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary"
        href="#api-reference"
      >
        {text.skipToReference}
      </a>
      <header className="border-b border-border bg-surface">
        <div className="mx-auto flex min-h-16 w-full max-w-6xl items-center justify-between gap-4 px-5 sm:px-8">
          <span className="text-sm font-semibold tracking-tight text-foreground">
            MyloStudio <span className="text-primary">API</span>
          </span>
          <div className="flex items-center gap-1 sm:gap-2">
            <a
              className="inline-flex min-h-10 items-center gap-2 rounded-lg px-3 text-sm font-medium text-muted transition hover:bg-elevated hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              download="mylostudio-openapi.yaml"
              href="/openapi.yaml"
            >
              <Icon className="size-4" name="document" />
              <span className="hidden sm:inline">{text.downloadOpenApi}</span>
              <span className="sm:hidden">OpenAPI</span>
            </a>
            <Link
              className="inline-flex min-h-10 items-center gap-2 rounded-lg px-3 text-sm font-medium text-muted transition hover:bg-elevated hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              to="/workspaces/developer-api"
            >
              <span aria-hidden="true">←</span>
              {text.back}
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl px-5 pb-16 pt-10 sm:px-8 sm:pt-14">
        <header className="border-b border-border pb-7">
          <p className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
            {text.eyebrow}
          </p>
          <h1 className="mt-2 text-balance text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            {text.title}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-muted">
            {text.description}
          </p>
        </header>

        <div
          className="grid gap-8 py-8 lg:grid-cols-[13rem_minmax(0,1fr)]"
          id="api-reference"
        >
          <aside className="lg:sticky lg:top-6 lg:self-start">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-subtle">
              {text.endpoints}
            </p>
            <nav aria-label={text.endpoints} className="grid gap-1">
              {endpointOrder.map(id => (
                <a
                  aria-current={activeEndpoint === id ? 'location' : undefined}
                  className={`grid min-h-12 grid-cols-[auto_1fr] items-center gap-x-2 rounded-xl px-3 py-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${activeEndpoint === id ? 'bg-primary/10 text-foreground' : 'text-muted hover:bg-elevated hover:text-foreground'}`}
                  href={`#${id}`}
                  key={id}
                >
                  <span
                    className={`rounded px-1.5 py-0.5 font-mono text-[10px] font-bold ${endpoints[id].accent}`}
                  >
                    {endpoints[id].method}
                  </span>
                  <span className="min-w-0 text-sm font-medium">
                    {metadata[id].navigationTitle}
                  </span>
                </a>
              ))}
            </nav>
          </aside>

          <div className="min-w-0">
            {endpointOrder.map((id, index) => {
              const endpoint = endpoints[id];
              const detail = metadata[id];
              return (
                <section
                  className={`scroll-mt-24 ${index === 0 ? '' : 'mt-14'}`}
                  id={id}
                  key={id}
                >
                  <section className="rounded-2xl border border-border bg-elevated p-5 sm:p-7">
                    <div className="flex flex-wrap items-center gap-3">
                      <span
                        className={`rounded-md px-2 py-1 font-mono text-xs font-bold ${endpoint.accent}`}
                      >
                        {endpoint.method}
                      </span>
                      <code
                        className="text-sm font-semibold text-foreground"
                        translate="no"
                      >
                        {endpoint.path}
                      </code>
                    </div>
                    <h2 className="mt-5 text-2xl font-semibold tracking-tight text-foreground">
                      {detail.title}
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-muted">
                      {detail.description}
                    </p>
                  </section>

                  {index === 0 ? (
                    <section className="mt-8">
                      <h2 className="text-lg font-semibold text-foreground">
                        {text.authentication}
                      </h2>
                      <p className="mt-2 max-w-3xl text-sm leading-6 text-muted">
                        {text.authDescription}
                      </p>
                      <pre className="mt-4 overflow-x-auto rounded-xl bg-foreground p-4 text-xs leading-6 text-surface">
                        <code translate="no">{text.authenticationCode}</code>
                      </pre>
                    </section>
                  ) : null}

                  {(['request', 'response'] as const).map(kind => (
                    <section className="mt-8" key={kind}>
                      <div className="flex items-center justify-between gap-4">
                        <h2 className="text-lg font-semibold text-foreground">
                          {kind === 'request' ? text.request : text.response}
                        </h2>
                        <button
                          className="inline-flex min-h-9 items-center gap-2 rounded-lg border border-border px-3 text-xs font-medium text-muted transition hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                          onClick={() => void copyCode(id, kind)}
                          type="button"
                        >
                          <Icon className="size-3.5" name="document" />
                          {copied === `${id}-${kind}` ? text.copied : text.copy}
                        </button>
                      </div>
                      <pre className="mt-3 overflow-x-auto rounded-xl bg-foreground p-4 text-xs leading-6 text-surface">
                        <code translate="no">{examples[id][kind]}</code>
                      </pre>
                    </section>
                  ))}

                  <section className="mt-8 rounded-2xl border border-primary/20 bg-primary/5 p-5">
                    <h2 className="font-semibold text-foreground">
                      {text.notes}
                    </h2>
                    <ul className="mt-3 grid gap-2 text-sm leading-6 text-muted">
                      {detail.notes.map(note => (
                        <li className="flex gap-2" key={note}>
                          <span aria-hidden="true" className="text-primary">
                            •
                          </span>
                          <span>{note}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                </section>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
};
